import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { 
  Library, 
  Plus, 
  Trash2, 
  ExternalLink, 
  Search,
  BookOpen,
  FileText,
  UploadCloud,
  X,
  ClipboardCheck,
  CalendarIcon
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";

export default function AdminBooks() {
  const [books, setBooks] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("all");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  const [formData, setFormData] = useState({
    title: "",
    author: "",
    category: "General",
    description: "",
    thumbnail: "",
    pdf_url: ""
  });
  
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchBooks();
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const res = await api.get("/courses");
      setCourses(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchBooks = async () => {
    try {
      const res = await api.get("/books");
      setBooks(res.data);
    } catch (err) {
      toast.error("Failed to load books");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload/file", formData);
      const file_url = res.data.url;

      setFormData(prev => ({ ...prev, [field]: file_url }));
      toast.success(`${field === 'pdf_url' ? 'PDF' : 'Thumbnail'} uploaded!`);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (selectedCourse !== "all") {
        payload.course_id = selectedCourse;
      }
      await api.post("/books", payload);
      toast.success("Book added successfully!");
      setFormData({ title: "", author: "", category: "General", description: "", thumbnail: "", pdf_url: "" });
      fetchBooks();
    } catch (err) {
      toast.error("Failed to add book");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await api.delete(`/books/${id}`);
      toast.success("Book removed");
      fetchBooks();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const filteredBooks = books.filter(b => {
    const matchSearch = b.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        b.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCourse = selectedCourse === "all" ? true : b.course_id === selectedCourse;
    return matchSearch && matchCourse;
  });

  return (
    <div className="p-6 lg:p-10 space-y-10 font-['Inter',sans-serif]">
      <Toaster position="top-center" />
      
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 flex items-center gap-3">
            <Library className="text-indigo-600" /> Digital Library
          </h1>
          <p className="text-slate-500 font-medium mt-1">Manage recommended reading materials for students</p>
        </div>
      </div>

      <section className="bg-white p-6 rounded-[1.5rem] border border-slate-100 shadow-sm">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-indigo-600">
          <ClipboardCheck size={20} /> Select Course
        </h3>
        <select
          className="w-full max-w-md px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-bold shadow-sm outline-none focus:ring-2 focus:ring-indigo-600/20"
          onChange={(e) => setSelectedCourse(e.target.value === "all" ? "all" : Number(e.target.value))}
          value={selectedCourse}
        >
          {courses.length === 0 && <option value="">No courses available</option>}
          <option value="all">-- View All Books (General) --</option>
          {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
      </section>

      {/* CREATE BOOK FORM */}
      <section className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden p-8 space-y-6">
        <h2 className="text-2xl font-black text-slate-900 mb-2">Add New Book</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Book Title</label>
              <input 
                required
                type="text" 
                className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600/10 font-bold"
                placeholder="e.g. Modern Banking"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Author Name</label>
              <input 
                required
                type="text" 
                className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600/10 font-bold"
                placeholder="e.g. Dr. John Doe"
                value={formData.author}
                onChange={(e) => setFormData({...formData, author: e.target.value})}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Category</label>
              <select 
                className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600/10 font-bold appearance-none"
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
              >
                <option value="General">General</option>
                <option value="Banking">Banking</option>
                <option value="SSC">SSC</option>
                <option value="Railway">Railway</option>
                <option value="UPSC">UPSC</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Description</label>
              <input 
                type="text" 
                className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-600/10 font-bold"
                placeholder="Brief overview..."
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Cover Image</label>
              <div className="relative group">
                <input 
                  type="file" 
                  accept="image/*"
                  className="hidden" 
                  id="thumb-upload"
                  onChange={(e) => { handleFileUpload(e, 'thumbnail'); e.target.value = null; }}
                />
                <label 
                  htmlFor="thumb-upload"
                  className="flex flex-col items-center justify-center gap-2 p-6 bg-slate-50 border-2 border-dashed border-slate-200 rounded-[2rem] cursor-pointer group-hover:border-indigo-600 transition-all"
                >
                  <UploadCloud className="text-slate-300 group-hover:text-indigo-600" size={32} />
                  <span className="text-[10px] font-black text-slate-400 uppercase group-hover:text-indigo-600">
                    {formData.thumbnail ? 'Image Selected' : 'Upload Thumbnail'}
                  </span>
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">PDF File</label>
              <div className="relative group">
                <input 
                  type="file" 
                  accept=".pdf"
                  className="hidden" 
                  id="pdf-upload"
                  onChange={(e) => { handleFileUpload(e, 'pdf_url'); e.target.value = null; }}
                />
                <label 
                  htmlFor="pdf-upload"
                  className="flex flex-col items-center justify-center gap-2 p-6 bg-slate-50 border-2 border-dashed border-slate-200 rounded-[2rem] cursor-pointer group-hover:border-indigo-600 transition-all"
                >
                  <FileText className="text-slate-300 group-hover:text-indigo-600" size={32} />
                  <span className="text-[10px] font-black text-slate-400 uppercase group-hover:text-indigo-600">
                    {formData.pdf_url ? 'PDF Selected' : 'Upload PDF'}
                  </span>
                </label>
              </div>
            </div>
          </div>

          <button 
            disabled={uploading}
            type="submit"
            className="w-full py-5 bg-indigo-600 text-white font-black rounded-2xl shadow-xl shadow-indigo-200 hover:bg-indigo-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {uploading ? 'Uploading...' : 'Publish Book'}
          </button>
        </form>
      </section>

      {/* 🔍 SEARCH & FILTERS */}
      <div className="relative max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
        <input 
          type="text"
          placeholder="Search by title or category..."
          className="w-full pl-12 pr-4 py-4 bg-white border border-slate-100 rounded-2xl shadow-sm outline-none focus:ring-2 focus:ring-indigo-600/10 transition-all font-medium"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* 📚 BOOKS TABLE */}
      <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50">
                <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Book Details</th>
                <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Category</th>
                <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Author</th>
                <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Created</th>
                <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredBooks.map((book) => (
                <tr key={book.id} className="hover:bg-slate-50/50 transition-colors group">
                  <td className="px-8 py-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-16 bg-slate-100 rounded-lg overflow-hidden shrink-0">
                        {book.thumbnail && <img src={book.thumbnail} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div>
                        <p className="font-black text-slate-900 group-hover:text-indigo-600 transition-colors">{book.title}</p>
                        <p className="text-xs font-medium text-slate-400 mt-1 line-clamp-1">{book.description}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-6">
                    <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-[10px] font-black uppercase tracking-widest border border-indigo-100">
                      {book.category}
                    </span>
                  </td>
                  <td className="px-8 py-6 font-bold text-slate-600">{book.author}</td>
                  <td className="px-8 py-6 text-sm text-slate-400">{new Date(book.created_at).toLocaleDateString()}</td>
                  <td className="px-8 py-6">
                    <div className="flex items-center gap-3">
                      <a href={book.pdf_url} target="_blank" rel="noreferrer" className="p-2 text-slate-400 hover:text-indigo-600 transition-colors">
                        <ExternalLink size={18} />
                      </a>
                      <button onClick={() => handleDelete(book.id)} className="p-2 text-slate-400 hover:text-red-500 transition-colors">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredBooks.length === 0 && !loading && (
            <div className="p-20 text-center text-slate-400 font-bold uppercase tracking-widest italic">
              No books found
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
